/**
 * Derived leg and trip-type utilities (frontend version).
 *
 * A "leg" is the full origin-to-destination journey (e.g. JFK→LAX),
 * which may consist of one or more connected flight segments (e.g.
 * JFK→ATL, ATL→LAX with a short layover). This module groups raw
 * segments into legs, then classifies the trip type.
 */

import type { FlightSegment } from '../types';

export interface Leg {
  origin: string;
  destination: string;
  departureDate: string;          // YYYY-MM-DD of first segment
  departureTime: string;          // HH:MM of first segment
  segments: FlightSegment[];
}

export type TripType = 'one_way' | 'roundtrip' | 'multi_city';

/** Maximum layover duration (in ms) for two segments to be considered part of the same leg. */
const MAX_LAYOVER_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Group consecutive connected segments into legs.
 *
 * Two consecutive segments belong to the same leg when:
 *   1. seg[n].destination === seg[n+1].origin (connected airports)
 *   2. The layover between seg[n].arrival and seg[n+1].departure < 24 hours
 *
 * Segments must be sorted by segment_number before calling this function.
 */
export function computeLegs(segments: FlightSegment[]): Leg[] {
  if (!segments || segments.length === 0) return [];

  const sorted = [...segments].sort((a, b) => a.segment_number - b.segment_number);

  // Prefer explicit leg boundaries from persisted segment metadata.
  const hasExplicitLegs = sorted.every(
    s => Number.isInteger(s.leg_number) && (s.leg_number || 0) > 0
  );
  if (hasExplicitLegs) {
    const legs: Leg[] = [];
    let currentLegNumber = sorted[0].leg_number!;
    let currentLegSegments: FlightSegment[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const curr = sorted[i];
      if (curr.leg_number === currentLegNumber) {
        currentLegSegments.push(curr);
      } else {
        legs.push(buildLeg(currentLegSegments));
        currentLegSegments = [curr];
        currentLegNumber = curr.leg_number!;
      }
    }

    legs.push(buildLeg(currentLegSegments));
    return legs;
  }

  const legs: Leg[] = [];
  let currentLegSegments: FlightSegment[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];

    const isConnected = prev.destination === curr.origin;
    const layoverMs = new Date(curr.departure_datetime).getTime() - new Date(prev.arrival_datetime).getTime();
    const isShortLayover = layoverMs >= 0 && layoverMs < MAX_LAYOVER_MS;

    if (isConnected && isShortLayover) {
      // Same leg (connection)
      currentLegSegments.push(curr);
    } else {
      // New leg
      legs.push(buildLeg(currentLegSegments));
      currentLegSegments = [curr];
    }
  }

  // Push final leg
  legs.push(buildLeg(currentLegSegments));
  return legs;
}

function buildLeg(segments: FlightSegment[]): Leg {
  const first = segments[0];
  const last = segments[segments.length - 1];
  const depParts = first.departure_datetime.split('T');

  return {
    origin: first.origin,
    destination: last.destination,
    departureDate: depParts[0],
    departureTime: depParts.length > 1 ? depParts[1].substring(0, 5) : '00:00',
    segments,
  };
}

/**
 * Classify trip type from legs:
 *   - 1 leg → one_way
 *   - 2 legs where leg[1].destination === leg[0].origin → roundtrip
 *   - Anything else → multi_city
 */
export function detectTripType(legs: Leg[]): TripType {
  if (legs.length <= 1) return 'one_way';
  if (legs.length === 2 && legs[1].destination === legs[0].origin) return 'roundtrip';
  return 'multi_city';
}

/** Convenience: compute legs and check if roundtrip in one call. */
export function isRoundtrip(segments: FlightSegment[]): boolean {
  return detectTripType(computeLegs(segments)) === 'roundtrip';
}
